import type { IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import {
    emptyMetrics,
    type AdHocAggregateResult,
    type AdHocAggregateRow,
    type AdHocTrendResult,
    type AnalyticsField,
    type AnalyticsFilter,
    type AnalyticsMetric,
    type OlapTable,
} from '@/shared/analytics/AnalyticsTypes';
import type { DashboardSqlShape, DashboardTileConfig } from '@/shared/assist/DashboardContract';
import { STAT_VIZ, tileStatMetrics } from './AIExplorerTileTypes';

const DEFAULT_FIELD: AnalyticsField = 'task_category';
const DEFAULT_SECONDARY: AnalyticsField = 'difficulty';
const DEFAULT_METRIC: AnalyticsMetric = 'turns';
const AGGREGATE_LIMIT = 12;
const MATRIX_ROW_LIMIT = 8;

/** The resolved data behind a tile, discriminated by how it renders. */
export type AIExplorerTileData =
    | { kind: 'chart'; aggregate: AdHocAggregateResult | null; trend: AdHocTrendResult | null }
    | { kind: 'matrix'; primary: AdHocAggregateResult; matrix: AdHocAggregateResult }
    | { kind: 'stat'; totals: Record<AnalyticsMetric, number>; metrics: AnalyticsMetric[] };

/**
 * Runs the analytics query behind a {@link DashboardTileConfig}, composing the
 * tile's own filters with the page's live ambient filters (the same composition
 * the assist chart and pinned tiles use). The semantic fields are the common
 * path — compiled to gateway calls — and a raw `sql` string is the power-user
 * fallback, sent straight to the OLAP endpoint via {@link IAnalyticsGateway.rawQuery}.
 */
export async function runAIExplorerTile(gateway: IAnalyticsGateway, config: DashboardTileConfig, pageFilters: AnalyticsFilter[]): Promise<AIExplorerTileData> {
    const q = config.query;
    const filters = [...pageFilters, ...(q.filters ?? [])];
    const viz = config.viz;
    const metric = q.metric ?? DEFAULT_METRIC;

    if (typeof q.sql === 'string' && q.sql.length > 0) {
        return runRawSql(gateway, config, q.sql, filters, q.sqlShape);
    }

    if (STAT_VIZ.has(viz)) {
        const metrics = tileStatMetrics(config);
        const result = await gateway.aggregate({ source: 'interactions', groupBy: [], metrics, filters });
        return { kind: 'stat', totals: result.totals, metrics };
    }

    if (viz === 'trend') {
        const trend = await gateway.trend({ source: 'interactions', bucket: q.bucket ?? 'week', metrics: [metric], filters });
        return { kind: 'chart', aggregate: null, trend };
    }

    if (viz === 'matrix') {
        const primaryField = q.primaryField ?? DEFAULT_FIELD;
        const secondaryField = q.secondaryField ?? DEFAULT_SECONDARY;
        const rowLimit = q.limit ?? MATRIX_ROW_LIMIT;
        const [primary, matrix] = await Promise.all([
            gateway.aggregate({ source: 'interactions', groupBy: [primaryField], metrics: ['turns', metric], orderBy: { metric: 'turns', direction: 'desc' }, filters, limit: rowLimit }),
            gateway.aggregate({ source: 'interactions', groupBy: [primaryField, secondaryField], metrics: ['turns'], filters }),
        ]);
        return { kind: 'matrix', primary, matrix };
    }

    const aggregate = await gateway.aggregate({
        source: 'interactions',
        groupBy: [q.primaryField ?? DEFAULT_FIELD],
        metrics: chartMetrics(metric),
        orderBy: { metric, direction: 'desc' },
        filters,
        limit: q.limit ?? AGGREGATE_LIMIT,
    });
    return { kind: 'chart', aggregate, trend: null };
}

function chartMetrics(metric: AnalyticsMetric): AnalyticsMetric[] {
    return metric === 'users' ? ['users'] : [metric, 'users'];
}

/**
 * Runs a tile's raw-SQL fallback and maps the generic table into the viz's data
 * shape. The active page + tile filters are forwarded as the declarative model;
 * telemetry compiles them into the leading `scoped` CTE, so a tile reading
 * `FROM scoped` stays as filter-reactive as the semantic ones without any SQL
 * being composed here.
 */
async function runRawSql(
    gateway: IAnalyticsGateway,
    config: DashboardTileConfig,
    sql: string,
    filters: AnalyticsFilter[],
    shape: DashboardSqlShape | undefined,
): Promise<AIExplorerTileData> {
    const table = await gateway.rawQuery(sql, filters);
    const viz = config.viz;
    const metric = config.query.metric ?? DEFAULT_METRIC;

    if (STAT_VIZ.has(viz)) {
        return statFromTable(table, tileStatMetrics(config));
    }
    if (viz === 'trend') {
        return { kind: 'chart', aggregate: null, trend: trendFromTable(table, shape, metric) };
    }
    if (viz === 'matrix') {
        throw new Error('Raw SQL is not supported for matrix tiles — use the semantic primaryField/secondaryField fields instead.');
    }
    return { kind: 'chart', aggregate: aggregateFromTable(table, config, shape, metric), trend: null };
}

function colName(table: OlapTable, preferred: string | undefined, fallbackIndex: number): string {
    if (preferred !== undefined && table.columns.includes(preferred)) return preferred;
    return table.columns[fallbackIndex] ?? table.columns[0] ?? '';
}

function num(value: unknown): number {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : 0;
}

function metricRecord(metric: AnalyticsMetric, value: number): Record<AnalyticsMetric, number> {
    const metrics = emptyMetrics();
    metrics[metric] = value;
    return metrics;
}

function aggregateFromTable(table: OlapTable, config: DashboardTileConfig, shape: DashboardSqlShape | undefined, metric: AnalyticsMetric): AdHocAggregateResult {
    const field: AnalyticsField = config.query.primaryField ?? DEFAULT_FIELD;
    const labelCol = colName(table, shape?.labelColumn, 0);
    const valueCol = colName(table, shape?.valueColumn, table.columns.length - 1);
    const rows: AdHocAggregateRow[] = table.rows.map((row) => ({
        keys: { [field]: String(row[labelCol] ?? '—') } as AdHocAggregateRow['keys'],
        metrics: metricRecord(metric, num(row[valueCol])),
    }));
    const total = rows.reduce((sum, row) => sum + row.metrics[metric], 0);
    return { rows, totals: metricRecord(metric, total), distinctCount: rows.length };
}

function trendFromTable(table: OlapTable, shape: DashboardSqlShape | undefined, metric: AnalyticsMetric): AdHocTrendResult {
    const bucketCol = colName(table, shape?.bucketColumn, 0);
    const valueCol = colName(table, shape?.valueColumn, table.columns.length - 1);
    const points = table.rows.map((row) => ({ bucket: String(row[bucketCol] ?? '—'), metrics: metricRecord(metric, num(row[valueCol])) }));
    const total = points.reduce((sum, point) => sum + point.metrics[metric], 0);
    return { points, totals: metricRecord(metric, total) };
}

function statFromTable(table: OlapTable, metrics: AnalyticsMetric[]): AIExplorerTileData {
    const row = table.rows[0] ?? {};
    const totals = emptyMetrics();
    metrics.forEach((metric, index) => {
        const column = table.columns.includes(metric) ? metric : table.columns[index] ?? table.columns[0] ?? '';
        totals[metric] = num(row[column]);
    });
    return { kind: 'stat', totals, metrics };
}
